// ProjectsViews — All projects as cards or as a table (pen "Projects and the
// Studio: in and out", T1 and its table scene), pure.
//
// A card is the project's front page in miniature: its outline in a
// preview, the name, counts · last change (never a format: that belongs to
// export, principle 5), "You were in <page>", and
// what waits for the author in words (never a count badge). Hovered or
// focused, it offers "Pick up at <page>"; clicking the card does the same.
// The open project (the one the author is in) is marked Open and offers
// "Back to <page>". A table row says the same in columns; its name opens it,
// and so does a click anywhere on the row.
//
// Props-driven: the items (with their actions) and their summaries come in.

export { ProjectCards, ProjectTable };

import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import type { MenuItem } from "../Menu/Menu.js";
import { RowOverflow } from "../RowOverflow/RowOverflow.js";
import { RowRename } from "../RowOverflow/RowRename.js";
import type { RecentItem } from "../Sidebar/sidebarEntries.js";
import type { ProjectForm, ProjectSummary } from "./projects.js";
import type {
    ProjectsSort,
    ProjectsView,
    ProjectsViewCommand,
} from "./projectsView.js";

type RowMenu = (item: RecentItem, pinned: boolean) => readonly MenuItem[];

// A summary for an item the Library no longer describes: nothing in it.
const blank = (item: RecentItem): ProjectSummary => ({
    id: item.id,
    title: item.title,
    archived: item.restore !== undefined,
    form: "free",
    modules: 0,
    pages: 0,
    outline: [],
    waiting: 0,
});

// The words every view uses for a project.
const useProjectWords = () => {
    const { t, i18n } = useLingui();
    const date = (iso: string): string => {
        const at = new Date(iso);
        if (Number.isNaN(at.getTime())) return "";
        const sameYear = at.getFullYear() === new Date().getFullYear();
        return new Intl.DateTimeFormat(i18n.locale, {
            day: "numeric",
            month: "short",
            ...(sameYear ? {} : { year: "numeric" }),
        }).format(at);
    };
    const counts = (s: ProjectSummary): readonly string[] => [
        t`${plural(s.modules, { one: "# module", other: "# modules" })}`,
        t`${plural(s.pages, { one: "# page", other: "# pages" })}`,
    ];
    return {
        date,
        contents: (s: ProjectSummary): string => counts(s).join(" · "),
        meta: (s: ProjectSummary): string => {
            const changed = s.changedAt === undefined ? "" : date(s.changedAt);
            return [
                ...counts(s),
                ...(changed === "" ? [] : [t`changed ${changed}`]),
            ].join(" · ");
        },
        waits: (n: number): string =>
            t`${plural(n, {
                one: "# thing waits for you",
                other: "# things wait for you",
            })}`,
        waitsShort: (n: number): string =>
            t`${plural(n, { one: "# thing", other: "# things" })}`,
    };
};

const FormIcon = ({ form }: { readonly form: ProjectForm }): ReactNode => (
    <Icon name={form === "course" ? "graduation-cap" : "folder-open"} />
);

// The project's outline in miniature (the card's preview).
const Preview = ({
    summary,
    title,
}: {
    readonly summary: ProjectSummary;
    readonly title: string;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <div className="bilda-projects__preview" aria-hidden="true">
            <span className="bilda-projects__preview-title">{title}</span>
            {summary.outline.length === 0 ? (
                <span className="bilda-projects__preview-empty">
                    {t`Empty, start writing`}
                </span>
            ) : (
                summary.outline.map((entry, at) => (
                    <span
                        // biome-ignore lint/suspicious/noArrayIndexKey: titles may repeat; the order is the identity
                        key={at}
                        className="bilda-projects__preview-row"
                    >
                        <Icon name={entry.container ? "folder" : "document"} />
                        <span className="bilda-projects__preview-text">
                            {entry.title}
                        </span>
                    </span>
                ))
            )}
        </div>
    );
};

const Card = ({
    item,
    summary,
    pinned,
    menu,
}: {
    readonly item: RecentItem;
    readonly summary: ProjectSummary;
    readonly pinned: boolean;
    readonly menu: RowMenu;
}): ReactNode => {
    const { t } = useLingui();
    const words = useProjectWords();
    const archived = item.restore !== undefined;
    const name =
        item.renaming === undefined ? (
            <span className="bilda-projects__name" title={item.title}>
                {item.title}
            </span>
        ) : (
            <RowRename
                initial={item.title}
                label={t`New name for ${item.title}`}
                onSubmit={item.renaming.onSubmit}
                onCancel={item.renaming.onCancel}
                className="bilda-projects__rename"
            />
        );
    const body = (
        <>
            <Preview summary={summary} title={item.title} />
            {item.current ? (
                <span className="bilda-projects__open-chip">
                    <span className="bilda-projects__open-dot" />
                    {t({
                        message: "Open",
                        context: "the project the author is in",
                    })}
                </span>
            ) : null}
            {name}
            <span className="bilda-projects__meta">
                {archived ? t`Archived` : words.meta(summary)}
            </span>
            {summary.youWereIn === undefined ? null : (
                <span className="bilda-projects__were">
                    <Icon name="corner-down-right" />
                    <span className="bilda-projects__were-text">
                        {t`You were in ${summary.youWereIn}`}
                    </span>
                </span>
            )}
            {summary.waiting === 0 ? null : (
                <span className="bilda-projects__waits">
                    <span className="bilda-projects__waits-dot" />
                    {words.waits(summary.waiting)}
                </span>
            )}
        </>
    );
    // Renaming: the field owns the card (no opening button around it, no
    // "…" to take the focus back).
    if (item.renaming !== undefined)
        return <li className="bilda-projects__card">{body}</li>;
    const page = summary.youWereIn;
    return (
        <li
            className="bilda-projects__card bilda-row-overflow-host"
            data-open={item.current ? "true" : undefined}
            data-archived={archived ? "true" : undefined}
        >
            <button
                type="button"
                className="bilda-projects__open"
                onClick={item.open}
            >
                {body}
            </button>
            {page === undefined || archived ? null : (
                <Button
                    variant="primary"
                    className="bilda-projects__pick-up"
                    onClick={item.open}
                >
                    {item.current ? t`Back to ${page}` : t`Pick up at ${page}`}
                </Button>
            )}
            <RowOverflow
                label={t`More options for ${item.title}`}
                items={menu(item, pinned)}
                className="bilda-projects__more"
            />
        </li>
    );
};

const ProjectCards = ({
    items,
    summaryOf,
    pinned,
    menu,
}: {
    readonly items: readonly RecentItem[];
    readonly summaryOf: (id: string) => ProjectSummary | undefined;
    readonly pinned: boolean;
    readonly menu: RowMenu;
}): ReactNode => (
    <ul className="bilda-projects__grid">
        {items.map((item) => (
            <Card
                key={item.id}
                item={item}
                summary={summaryOf(item.id) ?? blank(item)}
                pinned={pinned}
                menu={menu}
            />
        ))}
    </ul>
);

// A sortable column heading: a button; the sorted one names its order.
const SortHeading = ({
    label,
    sort,
    view,
    onView,
}: {
    readonly label: string;
    readonly sort: ProjectsSort;
    readonly view: ProjectsView;
    readonly onView: (cmd: ProjectsViewCommand) => void;
}): ReactNode => {
    const sorted = view.sort === sort;
    return (
        <th
            scope="col"
            aria-sort={
                sorted
                    ? view.order === "asc"
                        ? "ascending"
                        : "descending"
                    : undefined
            }
            data-sorted={sorted ? "true" : undefined}
        >
            <button
                type="button"
                className="bilda-projects__th-sort"
                onClick={() => onView({ type: "sort-by-column", sort })}
            >
                {label}
                {sorted ? (
                    <Icon
                        name={view.order === "asc" ? "arrow-up" : "arrow-down"}
                    />
                ) : null}
            </button>
        </th>
    );
};

const ProjectTable = ({
    items,
    summaryOf,
    pinnedIds,
    menu,
    view,
    onView,
}: {
    readonly items: readonly RecentItem[];
    readonly summaryOf: (id: string) => ProjectSummary | undefined;
    readonly pinnedIds: readonly string[];
    readonly menu: RowMenu;
    readonly view: ProjectsView;
    readonly onView: (cmd: ProjectsViewCommand) => void;
}): ReactNode => {
    const { t } = useLingui();
    const words = useProjectWords();
    return (
        <table className="bilda-projects__table">
            <thead>
                <tr>
                    <SortHeading
                        label={t`Name`}
                        sort="title"
                        view={view}
                        onView={onView}
                    />
                    <th scope="col">{t`Contents`}</th>
                    <th scope="col">{t`You were in`}</th>
                    <th scope="col">{t`Waits for you`}</th>
                    <SortHeading
                        label={t`Changed`}
                        sort="changed"
                        view={view}
                        onView={onView}
                    />
                </tr>
            </thead>
            <tbody>
                {items.map((item) => {
                    const s = summaryOf(item.id) ?? blank(item);
                    const archived = item.restore !== undefined;
                    return (
                        <tr
                            key={item.id}
                            className="bilda-projects__tr bilda-row-overflow-host"
                            data-open={item.current ? "true" : undefined}
                        >
                            <td>
                                <div className="bilda-projects__td-name">
                                    <span className="bilda-projects__td-icon">
                                        <FormIcon form={s.form} />
                                    </span>
                                    {item.renaming === undefined ? (
                                        <button
                                            type="button"
                                            className="bilda-projects__row-open"
                                            title={item.title}
                                            onClick={item.open}
                                        >
                                            {item.title}
                                        </button>
                                    ) : (
                                        <RowRename
                                            initial={item.title}
                                            label={t`New name for ${item.title}`}
                                            onSubmit={item.renaming.onSubmit}
                                            onCancel={item.renaming.onCancel}
                                            className="bilda-projects__rename"
                                        />
                                    )}
                                    {item.current ? (
                                        <span className="bilda-projects__open-chip">
                                            <span className="bilda-projects__open-dot" />
                                            {t({
                                                message: "Open",
                                                context:
                                                    "the project the author is in",
                                            })}
                                        </span>
                                    ) : null}
                                    {item.renaming === undefined ? (
                                        <RowOverflow
                                            label={t`More options for ${item.title}`}
                                            items={menu(
                                                item,
                                                pinnedIds.includes(item.id),
                                            )}
                                            className="bilda-projects__more"
                                        />
                                    ) : null}
                                </div>
                            </td>
                            <td>
                                {archived ? t`Archived` : words.contents(s)}
                            </td>
                            <td className="bilda-projects__td-were">
                                {s.youWereIn === undefined ? null : (
                                    <>
                                        <span className="bilda-projects__td-text">
                                            {s.youWereIn}
                                        </span>
                                        {archived ? null : (
                                            <button
                                                type="button"
                                                className="bilda-projects__td-pick"
                                                onClick={item.open}
                                            >
                                                {item.current
                                                    ? t`Back`
                                                    : t`Pick up`}
                                            </button>
                                        )}
                                    </>
                                )}
                            </td>
                            <td>
                                {s.waiting === 0 ? null : (
                                    <span className="bilda-projects__waits">
                                        <span className="bilda-projects__waits-dot" />
                                        {words.waitsShort(s.waiting)}
                                    </span>
                                )}
                            </td>
                            <td className="bilda-projects__td-date">
                                {s.changedAt === undefined
                                    ? null
                                    : words.date(s.changedAt)}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};
